// Simple email input for filtering emails by recipient address

import { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, useOpenClose } from '@evertrue/sdk';
import { Modal, ModalHeader, ModalBody, Icon, TextInput } from '@evertrue/et-components';
import { useOutreachFilterProvider } from '../../../contexts/outreach-filter-context';
import colors from 'base/colors';
import { isValidEmail } from '../../../utils/filter-search-helper';
import EverTrue from 'app';

const EmailSearchController = ({ onEmailAdd, onRefresh, selectedAddresses }) => {
  const searchBoxRef = useRef(null);
  const [query, setQuery] = useState('');
  const [removingEmail, setRemovingEmail] = useState(null); // Track which email is being removed
  const { setSelectedEmailAddresses } = useOutreachFilterProvider();

  const [isOpen, openModal, closeModal] = useOpenClose();

  const trimmedQuery = query?.trim();

  const clearInput = () => {
    if (searchBoxRef.current) {
      searchBoxRef.current.value = '';
    }
    setQuery('');
  };

  const handleRemoveEmail = useCallback(
    async (address) => {
      // Set loading state for this specific email
      setRemovingEmail(address.emailAddress);

      // Small delay to show the loading state
      await new Promise((resolve) => setTimeout(resolve, 500));

      setSelectedEmailAddresses((prev) => prev.filter((a) => a.emailAddress !== address.emailAddress));

      // Trigger immediate refresh
      onRefresh();

      // Clear loading state
      setRemovingEmail(null);
    },
    [setSelectedEmailAddresses, onRefresh]
  );

  const handleInputChange = (value) => {
    setQuery(value);
  };
  const handleKeyUp = (e) => {
    if (e.key === 'Enter' && trimmedQuery) {
      handleAddEmail();
    }
  };

  const handleAddEmail = useCallback(() => {
    if (!trimmedQuery) {
      EverTrue.Alert.error('Please enter an email address');
      return;
    }

    if (!isValidEmail(trimmedQuery)) {
      EverTrue.Alert.error('Please enter a valid email address');
      return;
    }

    // Check if email is already selected
    const isAlreadySelected = selectedAddresses.some(
      (addr) => addr.emailAddress.toLowerCase() === trimmedQuery.toLowerCase()
    );

    if (isAlreadySelected) {
      EverTrue.Alert.error('This email address is already selected');
      return;
    }

    // Create email object and add it
    const emailObject = {
      id: `email_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      emailAddress: trimmedQuery,
    };

    onEmailAdd(emailObject);
    clearInput();
  }, [trimmedQuery, selectedAddresses, onEmailAdd]);

  return (
    <>
      {/* Email address input */}
      <div className="email-search-container">
        <div className="email-input-wrapper">
          <Button type="primary" onClick={openModal} disabled={selectedAddresses.length === 0}>
            <Icon icon="email" size={1} />
            {selectedAddresses.length}
          </Button>

          <TextInput
            ref={searchBoxRef}
            value={query}
            onChange={handleInputChange}
            placeholder="Email Address Search..."
            onKeyUp={handleKeyUp}
            id="email-search-input"
            style={{ minWidth: '300px', maxWidth: '300px' }}
          />
          {trimmedQuery && (
            <Button type="primary" size="small" onClick={handleAddEmail} className="add-email-btn">
              <Icon icon="add" size={1} />
              Add
            </Button>
          )}
        </div>
      </div>

      {/* Edit selected addresses modal */}
      <Modal isOpen={isOpen} closeModal={closeModal} size="small" closeOnOutsideClick={true}>
        <ModalHeader title="Recipient Email Filters" />
        <ModalBody>
          These email addresses are currently applied as filters. Remove any you no longer want included in your
          results.
        </ModalBody>
        <div className="edit-selected-addresses-modal">
          {selectedAddresses.map((address, index) => {
            const isRemoving = removingEmail === address.emailAddress;

            return (
              <div className="email-card" key={address.emailAddress || index}>
                <div> {address.emailAddress}</div>
                <Button onClick={() => handleRemoveEmail(address)} type="warning" disabled={isRemoving}>
                  <Icon icon="trash" size={1} style={{ color: isRemoving ? colors.gray500 : colors.red500 }} />
                </Button>
              </div>
            );
          })}
        </div>
      </Modal>
    </>
  );
};

export default EmailSearchController;

EmailSearchController.propTypes = {
  onEmailAdd: PropTypes.func.isRequired, // Callback when email is added
  onRefresh: PropTypes.func.isRequired, // Callback to refresh filters
  selectedAddresses: PropTypes.array,
};
